import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';
import { usePerspective } from '../../contexts/PerspectiveContext';
import { getEffectiveScore } from '../../utils/scoreSourceUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  Building2, 
  FileText, 
  TrendingUp,
  AlertCircle,
  Plus,
  Search,
  Bell,
  Target,
  Calendar,
  Clock,
  AlertTriangle,
  Sparkles,
  ShieldAlert,
  ChevronRight,
  Users,
  DollarSign,
  ArrowUpRight,
  Loader2,
  Flame,
  Activity,
  BarChart3,
  UserPlus
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import InviteExpertModal from '../../components/modals/InviteExpertModal';
import { OrganizationHealthSummary } from '../../components/ai/OrganizationHealthSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// pWin color helper
const getPwinColor = (score) => {
  if (score >= 70) return { ring: 'stroke-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-700' };
  if (score >= 50) return { ring: 'stroke-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' };
  return { ring: 'stroke-red-500', text: 'text-red-600', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700' };
};

// Status badge colors
const getStatusStyle = (status) => {
  const styles = {
    'INTAKE': 'bg-slate-100 text-slate-700',
    'QUALIFICATION': 'bg-blue-100 text-blue-700',
    'STRATEGY': 'bg-purple-100 text-purple-700',
    'DRAFTING': 'bg-amber-100 text-amber-700',
    'SUBMITTED': 'bg-teal-100 text-teal-700',
    'WON': 'bg-emerald-100 text-emerald-700',
    'LOST': 'bg-red-100 text-red-700',
    'NO_BID': 'bg-slate-200 text-slate-600',
    'ARCHIVED': 'bg-slate-200 text-slate-500'
  };
  return styles[status] || 'bg-slate-100 text-slate-600';
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return '$0';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays <= 7) return `${diffDays} days`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// pWin circular indicator
// v3.11.21 — When `score` is null (pursuit not yet AI-analyzed), render a
// neutral dashed-ring placeholder with "—" so users immediately see that
// no real Fit Score exists for this row, instead of a misleading 0% / 45%
// drawn from raw `pwin_score`. The caller is expected to pass
// `getEffectiveScore(pursuit).score` (not raw `pwin_score`).
function PwinIndicator({ score }) {
  if (score === null || score === undefined) {
    return (
      <div
        className="relative w-12 h-12 rounded-full flex items-center justify-center bg-slate-50 border border-dashed border-slate-300"
        title="Run AI Analysis to calculate Fit Score"
        data-testid="pwin-indicator-empty"
      >
        <span className="text-xs font-semibold text-slate-400">—</span>
      </div>
    );
  }
  const { ring, text, bg } = getPwinColor(score);
  const circumference = 2 * Math.PI * 18;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <div className={cn("relative w-12 h-12 rounded-full flex items-center justify-center", bg)}>
      <svg className="absolute w-12 h-12 transform -rotate-90">
        <circle cx="24" cy="24" r="18" stroke="#e2e8f0" strokeWidth="3" fill="none" />
        <circle
          cx="24" cy="24" r="18" className={ring} strokeWidth="3" fill="none"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round"
        />
      </svg>
      <span className={cn("text-sm font-bold z-10", text)}>{score}%</span>
    </div>
  );
}

// pWin Heatmap Component
function PwinHeatmap({ pursuits }) {
  // v3.11.21 — Distribution buckets only count AI-analyzed pursuits; raw
  // `pwin_score` no longer leaks into the chart. Pursuits without an
  // analyzed score are surfaced as a fourth "Not analyzed" bucket so the
  // chart reflects portfolio reality (rather than shoving everyone into
  // 0-49% red just because pwin_score defaulted to 0).
  const scored = pursuits.map((p) => ({
    p,
    score: getEffectiveScore(p).score,
  }));
  const buckets = [
    { range: '70-100%', color: 'bg-emerald-500',
      count: scored.filter(({ score }) => score !== null && score >= 70).length },
    { range: '50-69%', color: 'bg-amber-500',
      count: scored.filter(({ score }) => score !== null && score >= 50 && score < 70).length },
    { range: '0-49%', color: 'bg-red-500',
      count: scored.filter(({ score }) => score !== null && score < 50).length },
    { range: 'Not analyzed', color: 'bg-slate-300',
      count: scored.filter(({ score }) => score === null).length },
  ];
  const total = pursuits.length || 1;

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Flame className="w-4 h-4 text-orange-500" />
          Fit Score Distribution
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {buckets.map((bucket) => (
            <div key={bucket.range} className="flex items-center gap-3">
              <span className="text-xs text-slate-500 w-16">{bucket.range}</span>
              <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={cn("h-full rounded-full transition-all", bucket.color)}
                  style={{ width: `${(bucket.count / total) * 100}%` }}
                />
              </div>
              <span className="text-sm font-semibold text-slate-700 w-8 text-right">{bucket.count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Pipeline Funnel Component
// v3.18.0 — Strategy column re-introduced now that Strategy-stage screens
// (Win Theme Registry + Section M Evaluation Factors) ship in v3.18.0.
// GO decisions now auto-promote QUALIFICATION → STRATEGY.
function PipelineFunnel({ pursuits }) {
  const stages = [
    { status: 'INTAKE', label: 'Intake', color: 'bg-slate-400' },
    { status: 'QUALIFICATION', label: 'Go / No-Go', color: 'bg-blue-500' },
    { status: 'STRATEGY', label: 'Strategy', color: 'bg-violet-500' },
    { status: 'DRAFTING', label: 'Drafting', color: 'bg-amber-500' },
    { status: 'SUBMITTED', label: 'Submitted', color: 'bg-teal-500' },
  ];

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
          <Activity className="w-4 h-4 text-blue-500" />
          Pipeline Funnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end justify-between gap-2 h-32">
          {stages.map((stage) => {
            const count = pursuits.filter(p => p.status === stage.status).length;
            const maxCount = Math.max(...stages.map(s => pursuits.filter(p => p.status === s.status).length), 1);
            const height = count > 0 ? Math.max((count / maxCount) * 100, 15) : 8;
            
            return (
              <div key={stage.status} className="flex flex-col items-center flex-1">
                <span className="text-sm font-bold text-slate-700 mb-1">{count}</span>
                <div 
                  className={cn("w-full rounded-t-md transition-all", stage.color)}
                  style={{ height: `${height}%`, minHeight: '8px' }}
                />
                <span className="text-xs text-slate-500 mt-2 text-center leading-tight">{stage.label}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CommandCenterDashboard() {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { user, authToken } = useAuth();
  const { enterPursuit } = usePursuit();
  const { getEffectiveRole, isInMockView } = usePerspective();
  
  const [loading, setLoading] = useState(true);
  const [pursuits, setPursuits] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [redFlags, setRedFlags] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showInviteExpertModal, setShowInviteExpertModal] = useState(false);

  // Use effective role for perspective switching support
  const effectiveRole = getEffectiveRole();
  
  // Determine if user is consultant (shows client names) or other role (hides client names)
  // Also show client column when Super Admin is viewing as Consultant
  const isConsultant = effectiveRole === ROLES.CONSULTANT;
  const isSuperAdmin = user?.role === ROLES.SUPER_ADMIN;
  const isSuperAdminInAdminView = user?.role === ROLES.SUPER_ADMIN && !isInMockView;
  const isContractorAdmin = user?.role === ROLES.CONTRACTOR_ADMIN;
  const isReviewer = user?.role === ROLES.REVIEWER;
  
  // Show client column for:
  // 1. Consultants (always need to see which client the pursuit belongs to)
  // 2. Super Admin (always manages multiple contractors, needs client context)
  const showClientColumn = isConsultant || isSuperAdmin;

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch pursuits
      const pursuitsRes = await fetch(`${API_URL}/api/pursuits`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (pursuitsRes.ok) {
        const pursuitsData = await pursuitsRes.json();
        // API returns array directly
        const pursuitsList = Array.isArray(pursuitsData) ? pursuitsData : (pursuitsData.pursuits || []);
        setPursuits(pursuitsList);
      }

      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/pursuits/stats`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    if (authToken) {
      fetchDashboardData();
    }
  }, [authToken, fetchDashboardData]);

  const handlePursuitClick = (pursuit) => {
    enterPursuit({
      id: pursuit.id,
      title: pursuit.opportunity_name,
      rfpNumber: pursuit.solicitation_number,
      client: pursuit.client_name || pursuit.organization_name
    });
    navigate(`/${workspaceId}/pursuit/${pursuit.id}/intake`);
  };

  // Filter pursuits based on search
  const filteredPursuits = pursuits.filter(p => 
    p.opportunity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.agency_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.solicitation_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get urgent deadline (soonest)
  // v3.10.4: dashboard aggregates now come from `/api/pursuits/stats`
  // (server-side, ACTIVE-only + respects access filters). Pre-3.10.4
  // these were computed client-side from the fetched `pursuits` list,
  // which meant WON/LOST pursuits were pulled into the pipeline value
  // and a long-tail of un-scored intake rows tanked the fit-score avg.
  const totalPipelineValue = stats?.pipeline_value ?? 0;
  // v3.11.20 — Avg Fit Score now mirrors per-pursuit Fit Score semantics.
  // `avg_pwin` is null from the backend when no pursuits have completed
  // AI analysis (was always 0 before, masking the empty state).
  // `avg_pwin_count` / `active_for_avg_pwin` are exposed so we can show
  // "N/M analyzed" — same UX as the Consultant Client Portfolio.
  const avgPwin = stats?.avg_pwin;
  const avgPwinCount = stats?.avg_pwin_count ?? 0;
  const avgPwinTotal = stats?.active_for_avg_pwin ?? (stats?.active ?? pursuits.length);
  const upcomingDeadlines = stats?.upcoming_deadlines ?? 0;
  const urgentPursuit = pursuits
    .filter(p => p.response_deadline && new Date(p.response_deadline) > new Date())
    .sort((a, b) => new Date(a.response_deadline) - new Date(b.response_deadline))[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="command-center-dashboard" className="min-h-screen bg-slate-50">
      {/* Header with Urgent Banner */}
      <div className="bg-white border-b border-slate-200">
        {/* Urgent Alert Banner */}
        {urgentPursuit && (
          <div className="bg-red-50 border-b border-red-100 px-8 py-2">
            <div className="flex items-center justify-center gap-2">
              <Bell className="w-4 h-4 text-red-500 animate-pulse" />
              <span className="text-sm font-medium text-red-700">
                URGENT: {urgentPursuit.agency_code} - {urgentPursuit.opportunity_name?.substring(0, 40)}... Due {formatDate(urgentPursuit.response_deadline)}
              </span>
              <button 
                className="text-xs text-red-600 hover:text-red-700 underline ml-2"
                onClick={() => handlePursuitClick(urgentPursuit)}
              >
                View Details
              </button>
            </div>
          </div>
        )}

        {/* Main Header */}
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">
                {isConsultant || (user?.role === ROLES.SUPER_ADMIN && isInMockView) 
                  ? 'Consultant Portal' 
                  : isSuperAdmin 
                    ? 'Admin Portal' 
                    : 'Command Center'}
              </p>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isConsultant || (user?.role === ROLES.SUPER_ADMIN && isInMockView) 
                  ? 'Portfolio Overview' 
                  : 'Pursuit Command Center'}
              </h1>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search pursuits..."
                  className="pl-10 w-64 border-slate-200 bg-white"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="dashboard-search"
                />
              </div>
              {/* Invite Expert Button - Contractor Admin Only */}
              {isContractorAdmin && (
                <Button 
                  variant="outline"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  onClick={() => setShowInviteExpertModal(true)}
                  data-testid="invite-expert-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Expert
                </Button>
              )}
              {!isReviewer && (
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => navigate(`/${workspaceId}/pipeline`)}
                  data-testid="new-bid-intake-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Pursuit
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">
        {/* Stats Row */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-pipeline-value">
            <CardContent className="p-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Total Pipeline Value
              </p>
              <p className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {formatCurrency(totalPipelineValue)}
              </p>
              <div className="flex items-center gap-1 text-emerald-600 text-sm">
                <ArrowUpRight className="w-4 h-4" />
                <span>{stats?.active ?? pursuits.length} active pursuit{(stats?.active ?? pursuits.length) === 1 ? '' : 's'}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-avg-pwin">
            <CardContent className="p-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Avg. Fit Score
              </p>
              <p className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="avg-pwin-value">
                {avgPwin !== null && avgPwin !== undefined ? `${avgPwin}%` : '—'}
              </p>
              <p className="text-sm text-slate-500">
                {avgPwin !== null && avgPwin !== undefined
                  ? `${avgPwinCount}/${avgPwinTotal} analyzed`
                  : 'No AI analysis yet'}
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-deadlines">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                    Deadlines (7 Days)
                  </p>
                  <p className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {upcomingDeadlines}
                  </p>
                  <p className={cn("text-sm", upcomingDeadlines > 0 ? "text-red-500" : "text-slate-500")}>
                    {upcomingDeadlines > 0 ? 'Action required' : 'All clear'}
                  </p>
                </div>
                {upcomingDeadlines > 0 && (
                  <Badge className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center p-0 text-xs">
                    {upcomingDeadlines}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm" data-testid="stat-win-rate">
            <CardContent className="p-6">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Win Rate (YTD)
              </p>
              <p className="text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats?.win_rate || '—'}%
              </p>
              <p className="text-sm text-slate-500">
                {stats?.wins || 0} won / {stats?.total_decided || 0} decided
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="flex gap-8">
          {/* Main Content Area */}
          <div className="flex-1">
            {/* Active Pursuits Table */}
            <Card className="border-slate-200 bg-white shadow-sm mb-8" data-testid="pursuits-table">
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Active Pursuits
                  </CardTitle>
                  <Badge className="bg-slate-100 text-slate-600 border-0">
                    {filteredPursuits.length} pursuits
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {/* Table Header - Conditional Client Column */}
                <div className={cn(
                  "grid gap-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-xs font-medium text-slate-500 uppercase tracking-wider",
                  showClientColumn ? "grid-cols-12" : "grid-cols-11"
                )}>
                  {showClientColumn && <div className="col-span-2">Client</div>}
                  <div className={showClientColumn ? "col-span-3" : "col-span-4"}>Opportunity</div>
                  <div className="col-span-2">Agency</div>
                  <div className="col-span-2">Status</div>
                  <div className="col-span-1 text-center">Fit</div>
                  <div className="col-span-2">Deadline</div>
                  <div className="col-span-2 text-right">Value</div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-slate-100">
                  {filteredPursuits.length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">
                        {isReviewer ? 'No assigned pursuits' : 'No pursuits found'}
                      </p>
                      {isReviewer ? (
                        <p className="text-sm text-slate-400 mt-2">
                          You will see pursuits here once you are assigned to review them
                        </p>
                      ) : (
                        <Button 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => navigate(`/${workspaceId}/pipeline`)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create First Pursuit
                        </Button>
                      )}
                    </div>
                  ) : (
                    filteredPursuits.map((pursuit) => (
                      <div 
                        key={pursuit.id}
                        className={cn(
                          "grid gap-4 px-6 py-4 hover:bg-slate-50 cursor-pointer transition-colors items-center",
                          showClientColumn ? "grid-cols-12" : "grid-cols-11"
                        )}
                        onClick={() => handlePursuitClick(pursuit)}
                        data-testid={`pursuit-row-${pursuit.id}`}
                      >
                        {/* Client Name - ONLY for Consultants or Super Admin in Consultant view */}
                        {showClientColumn && (
                          <div className="col-span-2 flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-slate-500" />
                            </div>
                            <span className="font-medium text-slate-900 text-sm truncate" data-testid="client-name-column">
                              {pursuit.client_name || pursuit.organization_name || 'Client'}
                            </span>
                          </div>
                        )}

                        {/* Opportunity Name */}
                        <div className={showClientColumn ? "col-span-3" : "col-span-4"}>
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {pursuit.opportunity_name}
                          </p>
                          <p className="text-xs text-slate-500">{pursuit.solicitation_number}</p>
                        </div>

                        {/* Agency */}
                        <div className="col-span-2">
                          <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">
                            {pursuit.agency_code || 'N/A'}
                          </Badge>
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          <Badge className={cn("text-xs font-medium border-0", getStatusStyle(pursuit.status))}>
                            {pursuit.status?.replace('_', ' ')}
                          </Badge>
                        </div>

                        {/* Fit Score */}
                        <div className="col-span-1 flex justify-center">
                          <PwinIndicator score={getEffectiveScore(pursuit).score} />
                        </div>

                        {/* Deadline */}
                        <div className="col-span-2">
                          <p className={cn(
                            "font-medium text-sm",
                            pursuit.response_deadline && new Date(pursuit.response_deadline) < new Date() 
                              ? "text-red-600" 
                              : "text-slate-700"
                          )}>
                            {formatDate(pursuit.response_deadline)}
                          </p>
                        </div>

                        {/* Value */}
                        <div className="col-span-2 text-right">
                          <p className="font-semibold text-slate-900">
                            {formatCurrency(pursuit.estimated_value)}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Visualization Row */}
            <div className="grid grid-cols-2 gap-6">
              <PipelineFunnel pursuits={filteredPursuits} />
              <PwinHeatmap pursuits={filteredPursuits} />
            </div>
          </div>

          {/* Sidebar - Upcoming Deadlines */}
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-8 space-y-6">
              {/* Organization Health Summary - AI Driven */}
              <OrganizationHealthSummary 
                organizationId={user?.organizationId}
                authToken={authToken}
                compact={true}
              />

              {/* Upcoming Deadlines */}
              <Card className="border-slate-200 shadow-sm" data-testid="upcoming-deadlines">
                <CardHeader className="pb-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    Upcoming Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {pursuits
                      .filter(p => p.response_deadline && new Date(p.response_deadline) > new Date())
                      .sort((a, b) => new Date(a.response_deadline) - new Date(b.response_deadline))
                      .slice(0, 5)
                      .map((pursuit) => {
                        const daysLeft = Math.ceil((new Date(pursuit.response_deadline) - new Date()) / (1000 * 60 * 60 * 24));
                        const isUrgent = daysLeft <= 7;
                        
                        return (
                          <div 
                            key={pursuit.id}
                            className={cn(
                              "p-3 rounded-lg border cursor-pointer transition-colors",
                              isUrgent ? "border-red-200 bg-red-50 hover:bg-red-100" : "border-slate-100 bg-white hover:bg-slate-50"
                            )}
                            onClick={() => handlePursuitClick(pursuit)}
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className={cn(
                                "text-xs font-medium",
                                isUrgent ? "text-red-600" : "text-blue-600"
                              )}>
                                {pursuit.agency_code}
                              </span>
                              <span className={cn(
                                "text-xs font-bold",
                                isUrgent ? "text-red-600" : "text-slate-600"
                              )}>
                                {daysLeft}d
                              </span>
                            </div>
                            <p className="text-sm font-medium text-slate-900 line-clamp-1">
                              {pursuit.opportunity_name}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {new Date(pursuit.response_deadline).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', year: 'numeric' 
                              })}
                            </p>
                          </div>
                        );
                      })}
                    
                    {pursuits.filter(p => p.response_deadline && new Date(p.response_deadline) > new Date()).length === 0 && (
                      <div className="text-center py-4">
                        <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No upcoming deadlines</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card className="border-slate-200 shadow-sm" data-testid="recent-activity">
                <CardHeader className="pb-3 border-b border-slate-100">
                  <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-purple-500" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {pursuits.slice(0, 4).map((pursuit, idx) => (
                      <div 
                        key={pursuit.id}
                        className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer"
                        onClick={() => handlePursuitClick(pursuit)}
                      >
                        <div className={cn(
                          "w-2 h-2 rounded-full mt-2",
                          idx === 0 ? "bg-emerald-500" : "bg-slate-300"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {pursuit.opportunity_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {pursuit.agency_code} • {pursuit.status?.replace('_', ' ')}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Expert Modal - Contractor Admin Only */}
      {isContractorAdmin && (
        <InviteExpertModal
          isOpen={showInviteExpertModal}
          onClose={() => setShowInviteExpertModal(false)}
          authToken={authToken}
          onSuccess={() => {
            // Optionally refresh data after successful invite
          }}
        />
      )}
    </div>
  );
}
